import { useParams, Link } from 'react-router-dom';
import MovieCard from '@/components/feature/MovieCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getMovieById, allMovies } from '@/mocks/movies';
import { categories } from '@/mocks/categories';

const categoryNames: Record<string, string> = {
  movie: '电影',
  tv: '电视剧',
  anime: '动漫',
  variety: '综艺',
};

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const movie = getMovieById(Number(id));

  if (!movie) {
    return (
      <main className="pt-16 min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100">
            <i className="ri-error-warning-line text-foreground-300 text-3xl"></i>
          </div>
          <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">影片未找到</h1>
          <p className="text-foreground-500 mb-6">抱歉，您要找的影片不存在或已下架</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-home-line"></i>
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const catName = categoryNames[movie.category] || movie.category;
  const relatedMovies = allMovies
    .filter(m => m.category === movie.category && m.id !== movie.id)
    .slice(0, 6);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Movie',
    name: movie.title,
    description: movie.description,
    image: movie.poster,
    datePublished: `${movie.year}-01-01`,
    director: {
      '@type': 'Person',
      name: movie.director,
    },
    actor: movie.actors.map(name => ({
      '@type': 'Person',
      name,
    })),
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: movie.rating,
      bestRating: '10',
      ratingCount: Math.floor(Math.random() * 100000) + 10000,
    },
    countryOfOrigin: {
      '@type': 'Country',
      name: movie.region,
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <title>{movie.title} - 红桃影院高清在线观看</title>
      <meta name="description" content={`${movie.title}在线观看，红桃影院提供${movie.title}高清免费播放。导演${movie.director}，主演${movie.actors.join('、')}。${movie.description}`} />
      <meta name="keywords" content={`${movie.title},${movie.title}在线观看,${movie.title}免费播放,${movie.title}高清,红桃影院`} />
      <meta property="og:title" content={`${movie.title} - 红桃影院高清在线观看`} />
      <meta property="og:description" content={movie.description} />
      <meta property="og:image" content={movie.poster} />
      <meta property="og:type" content="video.movie" />
      <meta property="og:url" content={`https://www.hongtao.tv/article/${movie.id}`} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={`${movie.title} - 红桃影院`} />
      <meta name="twitter:description" content={movie.description} />
      <meta name="twitter:image" content={movie.poster} />
      <link rel="canonical" href={`https://www.hongtao.tv/article/${movie.id}`} />

      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/60">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb items={[
              { label: '首页', to: '/' },
              { label: catName, to: `/category/${movie.category}` },
              { label: movie.title },
            ]} />
          </div>
        </div>

        <section className="w-full bg-background-50">
          <div className="relative w-full h-[300px] md:h-[450px] overflow-hidden">
            <img
              src={movie.banner}
              alt={movie.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/60 to-transparent"></div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 pb-14 md:pb-20 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8 -mt-32 relative z-10">
              <div className="lg:w-[300px] flex-shrink-0">
                <div className="aspect-[2/3] rounded-xl overflow-hidden">
                  <img
                    src={movie.poster}
                    alt={movie.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              <div className="flex-1 pt-4 md:pt-8">
                <div className="flex items-center flex-wrap gap-2 mb-3">
                  {movie.isNew && (
                    <span className="px-3 py-1 text-xs font-medium rounded-full bg-accent-500 text-background-50 whitespace-nowrap">
                      最新上线
                    </span>
                  )}
                  {movie.isHot && (
                    <span className="px-3 py-1 text-xs font-medium rounded-full bg-primary-100 text-primary-700 whitespace-nowrap">
                      热门推荐
                    </span>
                  )}
                  {movie.tags.map(tag => (
                    <Link
                      key={tag}
                      to={`/category/${movie.category}`}
                      className="px-3 py-1 text-xs font-medium rounded-full bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {tag}
                    </Link>
                  ))}
                </div>

                <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4">
                  {movie.title}
                </h1>

                <div className="flex items-center flex-wrap gap-4 mb-6 text-sm">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-star-fill text-accent-500"></i>
                    <strong className="text-foreground-900 text-lg">{movie.rating}</strong>
                    <span className="text-foreground-400">/ 10</span>
                  </span>
                  <span className="text-foreground-400">|</span>
                  <span className="text-foreground-600">{movie.year}</span>
                  <span className="text-foreground-400">|</span>
                  <span className="text-foreground-600">{movie.region}</span>
                  <span className="text-foreground-400">|</span>
                  <span className="text-foreground-600">{movie.duration || movie.episodes}</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-foreground-400 whitespace-nowrap">导演：</span>
                    <span className="text-foreground-800 font-medium">{movie.director}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-foreground-400 whitespace-nowrap">主演：</span>
                    <span className="text-foreground-800 font-medium">{movie.actors.join(' / ')}</span>
                  </div>
                </div>

                <div className="flex items-center gap-3 mb-8">
                  <button className="flex items-center gap-2 px-8 py-3 rounded-full bg-primary-500 text-background-50 font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-play-circle-fill text-lg"></i>
                    立即播放
                  </button>
                  <button className="w-11 h-11 flex items-center justify-center rounded-full border border-background-300 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer">
                    <i className="ri-heart-line text-lg"></i>
                  </button>
                  <button className="w-11 h-11 flex items-center justify-center rounded-full border border-background-300 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer">
                    <i className="ri-share-forward-line text-lg"></i>
                  </button>
                </div>

                {movie.updateInfo && (
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-accent-50 text-accent-800 text-sm mb-6">
                    <i className="ri-time-line"></i>
                    {movie.updateInfo}
                  </div>
                )}
              </div>
            </div>

            <div className="mt-10">
              <h2 className="font-heading text-xl font-bold text-foreground-900 mb-4">剧情简介</h2>
              <p className="text-foreground-600 leading-relaxed text-sm">
                {movie.longDescription}
              </p>
            </div>

            {relatedMovies.length > 0 && (
              <div className="mt-14">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-heading text-xl font-bold text-foreground-900">
                    相关推荐
                  </h2>
                  <Link
                    to={`/category/${movie.category}`}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap cursor-pointer"
                  >
                    查看更多
                    <i className="ri-arrow-right-line ml-1"></i>
                  </Link>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4" data-product-shop>
                  {relatedMovies.map(m => (
                    <MovieCard key={m.id} movie={m} variant="compact" />
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
}